import { useMutation } from '@apollo/react-hooks';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';

import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { EditorActions } from '@atlaskit/editor-core';

import { fg } from '@confluence/feature-gating';
import { useNativeCollabState } from '@confluence/native-collab';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { getMonitoringClient, setContentErrorAttributes } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { getStatusCode } from '@confluence/error-types';
import { retry } from '@confluence/retry';

import { EditorDraftSyncActionMutation } from './EditorDraftSyncActionMutation.graphql';
import type {
	EditorDraftSyncActionMutation as EditorDraftSyncActionMutationResponse,
	EditorDraftSyncActionMutationVariables,
} from './__types__/EditorDraftSyncActionMutation';
import { DraftSyncResponseError } from './useDraftSync';

export type SyncStatusMetadata = {
	status: string;
	version: number;
};

export type UseEditorDraftSyncActionProps = {
	contentId: string;
	editorPresetAPI?: PublicPluginAPI<[CollabEditPlugin]> | null | undefined;
	editorActions: EditorActions;
};

const UNKNOWN_ERROR_STR = 'Unknown Error from useEditorDraftSyncAction Mutation';
const DRAFT_SYNC_DEBOUNCE_TIME = 2500;

const parseSyncStatus = (syncStatus: string | number | boolean): SyncStatusMetadata | undefined => {
	// syncStatus is expected to follow this pattern: pending-{stepVersionNumber} | synced-{stepVersionNumber}
	if (typeof syncStatus !== 'string') {
		return undefined;
	}
	const parsed = syncStatus.split('-');
	return {
		status: parsed[0],
		version: Number(parsed[1]),
	};
};

export const useEditorDraftSyncAction = ({
	contentId,
	editorPresetAPI,
	editorActions,
}: UseEditorDraftSyncActionProps) => {
	const isLivePage = useIsCurrentPageLive();
	const [{ collabEditProvider, collabEditInitialized }, { setIsDraftSyncPending }] =
		useNativeCollabState();
	const [draftSyncActionError, setDraftSyncActionError] = useState<DraftSyncResponseError | null>(
		null,
	);
	const previousLocalChangeTimeRef = useRef<number | null | undefined>(undefined);

	const submitErrorToMonitoringSystem = useCallback(
		(error) => {
			setContentErrorAttributes(error, {
				objectId: contentId,
			});
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PAGE_MODES,
			});
		},
		[contentId],
	);

	const [editorDraftSyncActionMutation] = useMutation<
		EditorDraftSyncActionMutationResponse,
		EditorDraftSyncActionMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		EditorDraftSyncActionMutation,
	);

	const executeDraftSyncActionMutation = useCallback(async () => {
		const currentStep = collabEditProvider?.getCurrentPmVersion();
		const latestAdf = await editorActions.getValue();
		const { data } = await editorDraftSyncActionMutation({
			variables: {
				input: {
					contentId,
					ncsStepVersion: currentStep,
					doSetRelations: true, // Collaborator relation is an entity on the backend used to indicate if a user made changes to that version
					latestAdf: JSON.stringify(latestAdf),
				},
			},
		});
		return data;
	}, [contentId, editorDraftSyncActionMutation, collabEditProvider, editorActions]);

	const saveDraft = useCallback(async () => {
		setIsDraftSyncPending(true);
		try {
			const data = await retry(executeDraftSyncActionMutation, { maxAttempts: 2, timeout: 2000 });
			const result = data?.editorDraftSyncAction;

			if (result?.success) {
				setDraftSyncActionError(null);
				setIsDraftSyncPending(false);
				return;
			}

			if (result?.errors?.length && result?.errors?.length > 0) {
				result.errors.forEach((error) => {
					if (error.message && error.extensions && error.extensions[0]?.statusCode) {
						setDraftSyncActionError(
							new DraftSyncResponseError(error.message, error.extensions[0].statusCode),
						);
					} else {
						setDraftSyncActionError(
							new DraftSyncResponseError(error?.message || UNKNOWN_ERROR_STR, undefined),
						);
					}
					submitErrorToMonitoringSystem(error);
				});
				return;
			}
		} catch (error) {
			setDraftSyncActionError(
				new DraftSyncResponseError(
					error?.message || UNKNOWN_ERROR_STR,
					Number(getStatusCode(error)),
				),
			);
			submitErrorToMonitoringSystem(error);
			markErrorAsHandled(error);
		}
	}, [submitErrorToMonitoringSystem, setIsDraftSyncPending, executeDraftSyncActionMutation]);

	const onUpdate = useCallback(() => {
		if (
			!isLivePage ||
			!collabEditProvider ||
			!collabEditInitialized ||
			!fg('confluence_frontend_draft_sync_v2')
		) {
			return;
		}

		const metadata = collabEditProvider.getMetadata();
		const collabSyncStatus = parseSyncStatus(metadata['syncStatus']);

		if (collabSyncStatus) {
			const currentStatus = collabSyncStatus['status'];
			if (currentStatus === 'synced') {
				// BE is not currently saving, so send request
				void saveDraft();
			}
			if (currentStatus === 'pending') {
				// implies BE is already saving, so we don't send a new request
				return;
			}
		} else {
			// collabSyncStatus is set by the BE as a result of draft sync. If it doesn't exist, BE needs to save and set it.
			void saveDraft();
		}
	}, [isLivePage, collabEditProvider, collabEditInitialized, saveDraft]);

	const debouncedOnUpdate = useMemo(
		() => debounce(onUpdate, DRAFT_SYNC_DEBOUNCE_TIME, { trailing: true }),
		[onUpdate],
	);

	const triggerUpdate = useCallback(() => {
		if (isLivePage && fg('confluence_frontend_draft_sync_v2')) {
			void debouncedOnUpdate();
		}
	}, [debouncedOnUpdate, isLivePage]);

	const cancelUpdate = useCallback(() => debouncedOnUpdate.cancel(), [debouncedOnUpdate]);
	const flushUpdate = useCallback(() => {
		void debouncedOnUpdate.flush();
	}, [debouncedOnUpdate]);

	const handlePluginStateEffect = useCallback(
		({ collabEditState }) => {
			if (!isLivePage || !fg('confluence_frontend_draft_sync_v2')) {
				return;
			}
			if (
				!collabEditState?.initialised?.lastLocalOrganicBodyChangeAt ||
				collabEditState?.initialised?.lastLocalOrganicBodyChangeAt ===
					previousLocalChangeTimeRef.current
			) {
				// Implies no user changes have been made
				return;
			}
			previousLocalChangeTimeRef.current = collabEditState.initialised.lastLocalOrganicBodyChangeAt;
			triggerUpdate();
		},
		[isLivePage, triggerUpdate],
	);
	usePluginStateEffect(editorPresetAPI, ['collabEdit'], handlePluginStateEffect);

	useEffect(() => {
		if (
			!isLivePage ||
			!collabEditProvider ||
			!collabEditInitialized ||
			!fg('confluence_frontend_draft_sync_v2')
		) {
			return;
		}
		window.addEventListener('beforeunload', flushUpdate);
		return () => {
			window.removeEventListener('beforeunload', flushUpdate);
		};
	}, [collabEditInitialized, collabEditProvider, isLivePage, flushUpdate]);

	return {
		triggerUpdate,
		cancelUpdate,
		flushUpdate,
		draftSyncActionError,
	};
};
